import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FlexColumn,
  Button as BaseButton,
  SettingsPage,
  FlexMiddle,
  Search,
  Table,
  ContextMenu
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import privileges from '@ubeya/shared-web/privileges';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import useTranslatedOptions from '@ubeya/shared/hooks/useTranslatedOptions';
import { ReactComponent as ExcelIcon } from '@ubeya/shared-web/assets/file-excel.svg';
import { CostCenterModal } from '.';
import { EmptyState } from '../EmptyState';
import CostCenterActionsRenderer from './CostCenterActionsRenderer';
import { ImportCostCenters } from './ImportCostCenters';

const Button = styled(BaseButton)`
  margin-left: 10px;
`;

const Content = styled(FlexColumn)`
  flex: 1;
  height: 100%;
  margin-bottom: 20px;
  overflow: auto;
`;

const CostCentersSection = ({ costCenters, isLoading, addCostCenter, updateCostCenter, deleteCostCenter }) => {
  const { t } = useContext(ConfigContext);
  const { isUbeyaAdmin } = useUser();
  const myPrivileges = useContext(PrivilegesContext);

  const [search, setSearch] = useState('');
  const [showCostCenter, setShowCostCenter] = useState();
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const filteredCostCenters = useMemo(
    () =>
      costCenters
        .filter(
          ({ name, identifier }) =>
            (name || '').toLowerCase().includes(search.toLowerCase()) ||
            (identifier || '').toLowerCase().includes(search.toLowerCase())
        )
        .map(({ id, name, identifier }) => ({
          id,
          name,
          identifier
        })),
    [costCenters, search]
  );

  const columns = useMemo(
    () => [
      {
        Header: t('costCenterName'),
        dataKey: 'name'
      },
      {
        Header: t('costCenterIdentifier'),
        dataKey: 'identifier'
      }
    ],
    [t]
  );

  const hasEditPermissions = myPrivileges[privileges.EDIT_PERMISSIONS] || isUbeyaAdmin;

  const contextMenuOptions = useTranslatedOptions([
    {
      label: 'importCostCenters',
      onClick: () => setIsImportModalOpen(true),
      icon: ExcelIcon,
      visible: hasEditPermissions
    }
  ]);

  const onCloseImport = () => setIsImportModalOpen(false);

  return (
    <Content>
      <SettingsPage
        scrollable
        innerScroll={false}
        title={t('costCenters')}
        actionBar={
          <FlexMiddle>
            {costCenters.length > 0 && <Search value={search} onChange={setSearch} />}
            <Button
              secondary
              narrow
              onClick={(e) => {
                e.preventDefault();
                setShowCostCenter(true);
              }}>
              {t('addCostCenter')}
            </Button>
            {hasEditPermissions && <ContextMenu options={contextMenuOptions} />}
          </FlexMiddle>
        }>
        {!isLoading && filteredCostCenters.length === 0 ? (
          <EmptyState text={t('emptyStateCostCenters')}>
            <BaseButton
              secondary
              onClick={(e) => {
                e.preventDefault();
                setShowCostCenter(true);
              }}>
              {t('addCostCenter')}
            </BaseButton>
          </EmptyState>
        ) : (
          <Table
            headerRowHeight={50}
            autoHeight={true}
            columns={columns}
            data={filteredCostCenters}
            onRowClick={({ rowData: costCenter }) => setShowCostCenter(costCenter)}
            {...(isUbeyaAdmin && {
              actionRenderer: ({ rowData }) => (
                <CostCenterActionsRenderer
                  rowData={rowData}
                  columnData={{
                    onEdit: (costCenter) => setShowCostCenter(costCenter),
                    onDelete: (costCenter) => deleteCostCenter({ costCenterId: costCenter.id })
                  }}
                />
              )
            })}
          />
        )}
      </SettingsPage>

      {showCostCenter && (
        <CostCenterModal
          addCostCenter={addCostCenter}
          selectedCostCenter={showCostCenter}
          updateCostCenter={updateCostCenter}
          onClose={() => setShowCostCenter(false)}
        />
      )}

      {isImportModalOpen && <ImportCostCenters onClose={onCloseImport} />}
    </Content>
  );
};

export default CostCentersSection;
